import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { StorageItem } from '@vsolv/packages/storage/domain';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { DialogComponent } from '@vsolv/vectors-ui/dialog';
import { Claim } from '@wsphere/warranties/domain';
import { BehaviorSubject } from 'rxjs';
import { CLAIM_ITEM_CONTROLS_CONFIG } from '../../config/claim-item-controls.config';
import { ClaimService } from '../../services';
import { escapeHtml } from '../../utils/escape-html.util';
import { fileThumbUrl, fileTypeLabel } from '../../utils/file-thumb.util';

export interface ClaimItemHoldCompletion {
  note: string | null;
  documentIds: string[];
}

/** Complete closes the hold; attach only links more files to it. */
export type ClaimItemHoldEvidenceMode = 'complete' | 'attach';

export interface ClaimItemHoldEvidenceOptions {
  mode?: ClaimItemHoldEvidenceMode;
  /** Files already linked to the hold; hidden from the picker and counted toward the limit. */
  linked?: Claim.ClaimItem.Hold.EvidenceFile[];
}

/** Picks an optional note and files to complete a hold, or just files to attach. The caller saves. */
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-claim-item-hold-complete-dialog',
  templateUrl: './claim-item-hold-complete.dialog.html',
  standalone: false,
})
export class ClaimItemHoldCompleteDialog {
  constructor(
    private claimSvc: ClaimService,
    private toastSvc: ToastService
  ) {}

  readonly config = CLAIM_ITEM_CONTROLS_CONFIG;

  /** Same file types as the claim's Attachments upload; the chooser only hints, so upload() checks too. */
  readonly accept = '.png,.jpg,.jpeg,.gif,.pdf';
  private readonly acceptedTypes = ['image/png', 'image/jpeg', 'image/gif', 'application/pdf'];

  /** The server rejects more than this per hold, counting files already linked. */
  readonly maxFiles = Claim.ClaimItem.Hold.HOLD_EVIDENCE_MAX_FILES;
  readonly maxFilesText = this.config.completeHoldDialog.maxFiles(this.maxFiles);

  @ViewChild(DialogComponent) dialog?: DialogComponent;

  @Input() claim: Claim.Model | null = null;

  /** Only hides the file picker; the server checks too. */
  @Input() canViewFiles = false;

  /** Only hides the upload button; the server checks too. */
  @Input() canUploadFiles = false;

  @Output() confirmed = new EventEmitter<ClaimItemHoldCompletion>();

  /** Null while the claim's files are loading. */
  readonly files$ = new BehaviorSubject<StorageItem.Model[] | null>(null);

  /** Every claim file, linked ones too, for the thumbnails. */
  filesById = new Map<string, StorageItem.Model>();

  readonly typeLabel = fileTypeLabel;

  /** Files uploaded here have no link to show yet, so they preview from the picked file. */
  private previews = new Map<string, string>();

  note = new FormControl('');
  documentIds = new FormControl<string[]>([], { nonNullable: true });

  uploading = false;

  mode: ClaimItemHoldEvidenceMode = 'complete';
  linked: Claim.ClaimItem.Hold.EvidenceFile[] = [];

  get attaching() {
    return this.mode === 'attach';
  }

  get title() {
    return this.attaching ? this.config.completeHoldDialog.attach.title : this.config.completeHoldDialog.title;
  }

  get saveLabel() {
    return this.attaching ? this.config.completeHoldDialog.attach.save : this.config.completeHoldDialog.save;
  }

  get closeLabel() {
    return this.attaching ? this.config.completeHoldDialog.attach.cancel : this.config.completeHoldDialog.back;
  }

  /** How many more files the hold can take. */
  get room() {
    return Math.max(this.maxFiles - this.linked.length, 0);
  }

  thumbFor(file: StorageItem.Model) {
    return this.previews.get(file.id) ?? fileThumbUrl(file);
  }

  /** Too many files ticked, or attaching to a hold that's already full. */
  get overLimit() {
    return this.documentIds.value.length > this.room || (this.attaching && this.room === 0);
  }

  get canConfirm() {
    const picked = this.documentIds.value.length;
    return !this.uploading && picked <= this.room && (!this.attaching || picked > 0);
  }

  async open({ mode = 'complete', linked = [] }: ClaimItemHoldEvidenceOptions = {}) {
    this.mode = mode;
    this.linked = linked;
    this.note.reset('');
    this.documentIds.reset([]);
    this.files$.next(null);
    this.filesById = new Map();
    this.previews.forEach(url => URL.revokeObjectURL(url));
    this.previews.clear();
    this.dialog?.open();

    if (!this.canViewFiles || !this.claim) return;
    const linkedIds = new Set(linked.map(file => file.storageItemId));
    const files = await this.claimSvc.listAllDocuments(this.claim.id).catch(() => []);
    this.filesById = new Map(files.map(file => [file.id, file]));
    this.files$.next(files.filter(file => !linkedIds.has(file.id)));
  }

  /** Uploads to the claim's Attachments (staff only) and ticks the new files. */
  async upload(input: HTMLInputElement) {
    const files = Array.from(input.files ?? []);
    input.value = '';
    if (!files.length || !this.claim || this.uploading) return;

    const { fileError, wrongType } = this.config.completeHoldDialog;
    const refused = files.find(file => !this.acceptedTypes.includes(file.type));
    if (refused) return this.uploadError(fileError(refused.name, wrongType));
    if (this.documentIds.value.length + files.length > this.room) return this.uploadError(this.maxFilesText);

    this.uploading = true;
    for (const file of files) {
      const id = await this.claimSvc
        .createDocument(this.claim.id, { file, title: file.name, feed: ['staff'] })
        .catch((error: Error) => {
          this.uploadError(fileError(file.name, error.message));
          return null;
        });
      if (!id) continue;

      const added = { id, title: file.name, originalFilename: file.name, mimeType: file.type } as StorageItem.Model;
      if (file.type.startsWith('image/')) this.previews.set(id, URL.createObjectURL(file));
      this.files$.next([...(this.files$.value ?? []), added]);
      this.documentIds.setValue([...this.documentIds.value, id]);
    }
    this.uploading = false;
  }

  /** Escaped because toasts render HTML and file names come from the user. */
  private uploadError(text: string) {
    this.toastSvc.show({ type: 'error', title: this.config.completeHoldDialog.uploadFailed, text: escapeHtml(text) });
  }

  close() {
    this.dialog?.close();
  }

  confirm() {
    const note = this.attaching ? null : this.note.value?.trim() || null;
    this.confirmed.emit({ note, documentIds: [...this.documentIds.value] });
    this.close();
  }
}
